import type { UserAttributes } from '@lib/analytics/AnalyticsService'
import {
    ATTRIBUTION_PARAM_KEYS,
    PII_KEYS,
    TRACK_ONCE_KEYS,
} from '@lib/analytics/constants'
import { hasCookieConsent } from '@lib/hooks/useCookieConsent'
import * as Sentry from '@sentry/nextjs'
import { isPrd } from '@shared/util/env'
import mixpanel from 'mixpanel-browser'

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN
type EventProperties = Record<string, any>

/**
 * Clear PII from super properties (one-time migration)
 * This ensures existing users don't have PII in their super properties
 */
export const clearPiiFromSuperProperties = () => {
    try {
        PII_KEYS.forEach((key) => {
            mixpanel.unregister(key)
        })
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'clear_pii_failed',
                analytics: 'mixpanel',
            },
        })
        if (!isPrd()) {
            console.warn('Failed to clear PII from super properties:', error)
        }
    }
}

export const init = (searchParams?: URLSearchParams) => {
    if (!MIXPANEL_TOKEN) {
        Sentry.captureMessage('Mixpanel token is missing', {
            level: 'warning',
            tags: {
                errorType: 'missing_token',
                analytics: 'mixpanel',
            },
        })
        if (!isPrd()) {
            console.warn('Mixpanel token is missing! Check your .env file.')
        }
        return
    }

    mixpanel.init(MIXPANEL_TOKEN, {
        api_host: '/a/mp', // proxy through Next.js rewrite to bypass ad blockers
        autocapture: false,
        // Session recording: set NEXT_PUBLIC_ENABLE_MIXPANEL_SESSION_RECORDING=true in production
        record_sessions_percent:
            process.env.NEXT_PUBLIC_ENABLE_MIXPANEL_SESSION_RECORDING === 'true'
                ? 1
                : 0,
        record_idle_timeout_ms: 300000, // stop recording after 5 minutes idle
        record_block_class: 'mp-sensitive', // block elements with this class
        record_mask_text_selector: '.mp-mask', // mask text in elements with this class
        record_max_ms: 1800000, // max 30 minutes of recording per session
        // Heatmaps: set NEXT_PUBLIC_ENABLE_MIXPANEL_HEATMAPS=true in production
        record_heatmap_data:
            process.env.NEXT_PUBLIC_ENABLE_MIXPANEL_HEATMAPS === 'true',
    } as any)

    // Clear any existing PII from super properties (migration for existing users)
    clearPiiFromSuperProperties()

    // Register attribution parameters if provided
    if (searchParams) {
        registerAttributionParameters(searchParams)
    }
}

const registerAttributionParameters = (searchParams: URLSearchParams) => {
    try {
        const attributionParams: Record<string, string> = {}

        ATTRIBUTION_PARAM_KEYS.forEach((param) => {
            const value = searchParams.get(param)
            if (value) {
                attributionParams[param] = value
            }
        })

        // Only register if we have attribution parameters
        if (Object.keys(attributionParams).length > 0) {
            // Use register to always capture the latest attribution (last-touch)
            mixpanel.register(attributionParams)

            // Set first-touch attribution as user properties (never overwritten)
            // These persist on the user profile and won't change on subsequent visits
            const originalAttributionParams: Record<string, string> = {}
            Object.entries(attributionParams).forEach(([key, value]) => {
                originalAttributionParams[`original_${key}`] = value
            })
            mixpanel.people.set_once(originalAttributionParams)
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'register_attribution_failed',
                analytics: 'mixpanel',
            },
        })
        if (!isPrd()) {
            console.warn('Failed to register attribution parameters:', error)
        }
    }
}

/**
 * Register flow type as a super property.
 * Super properties are automatically included in ALL events and persist across sessions.
 * This ensures flowType is available for funnel filtering in Mixpanel reports.
 */
export const registerFlowType = (flowType: string) => {
    try {
        if (!hasCookieConsent()) {
            return
        }
        mixpanel.register({ flowType })
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'register_flow_type_failed',
                analytics: 'mixpanel',
            },
            extra: { flowType },
        })
        if (!isPrd()) {
            console.warn('Failed to register flow type:', error)
        }
    }
}

export const identifyUser = (userId: string) => {
    try {
        // Note: Consent check handled by AnalyticsService before calling this
        mixpanel.identify(userId)
        mixpanel.people.set({
            $browser: navigator.userAgent,
        })
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'identify_user_failed',
                analytics: 'mixpanel',
            },
        })
        if (!isPrd()) {
            console.warn('Failed to identify Mixpanel user:', error)
        }
    }
}

export const updateAttributes = (userAttributes: UserAttributes) => {
    // Note: Consent check handled by AnalyticsService before calling this

    // Separate PII from non-PII attributes
    const piiAttributes: Partial<UserAttributes> = {}
    const nonPiiAttributes: Partial<UserAttributes> = {}

    Object.entries(userAttributes).forEach(([key, value]) => {
        if (PII_KEYS.includes(key)) {
            piiAttributes[key as keyof UserAttributes] = value
        } else {
            nonPiiAttributes[key as keyof UserAttributes] = value
        }
    })

    // Store PII in user profile ONLY (not in events)
    if (Object.keys(piiAttributes).length > 0) {
        mixpanel.people.set(piiAttributes)
    }

    // Register non-PII as super properties (will be included in events)
    // Split into once-only vs updatable
    const onceOnlyAttributes: Partial<UserAttributes> = {}
    const updatableAttributes: Partial<UserAttributes> = {}

    Object.entries(nonPiiAttributes).forEach(([key, value]) => {
        if (TRACK_ONCE_KEYS.includes(key)) {
            const attrs = onceOnlyAttributes as Record<string, unknown>
            attrs[key] = value
        } else {
            const attrs = updatableAttributes as Record<string, unknown>
            attrs[key] = value
        }
    })

    // Register attributes that should only be set once
    if (Object.keys(onceOnlyAttributes).length > 0) {
        mixpanel.register_once(onceOnlyAttributes)
    }

    // Register attributes that can be updated
    if (Object.keys(updatableAttributes).length > 0) {
        mixpanel.register(updatableAttributes)
    }
}

export const track = (eventName: string, properties: EventProperties = {}) => {
    try {
        if (!eventName) {
            if (!isPrd()) {
                console.warn('Event name is required for tracking')
            }
            return
        }

        // Note: Consent check handled by AnalyticsService before calling this

        const baseProperties = {
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
        }

        mixpanel.track(eventName, {
            ...baseProperties,
            ...properties,
        })
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'track_event_failed',
                analytics: 'mixpanel',
            },
            extra: { eventName },
        })
        if (!isPrd()) {
            console.warn('Failed to track event:', eventName, error)
        }
    }
}

export const startSessionRecording = () => {
    try {
        // Note: Consent check handled by AnalyticsService before calling this
        mixpanel.start_session_recording()
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'start_session_recording_failed',
                analytics: 'mixpanel',
            },
        })
        if (!isPrd()) {
            console.warn('Failed to start session recording:', error)
        }
    }
}

export const stopSessionRecording = () => {
    try {
        mixpanel.stop_session_recording()
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'stop_session_recording_failed',
                analytics: 'mixpanel',
            },
        })
        if (!isPrd()) {
            console.warn('Failed to stop session recording:', error)
        }
    }
}
